/* ============================================================
   dp-base.js — core helpers loaded on DP templates only
   Exposes window.DP for module use and sets up reveal animations
   and the cart AJAX client.
   ============================================================ */
(function () {
  'use strict';

  // ---------- Design mode flag ----------
  const isDesignMode = !!(window.Shopify && window.Shopify.designMode);
  if (isDesignMode) {
    document.documentElement.classList.add('dp-design-mode');
  }

  // ---------- Namespace ----------
  const DP = window.DP = window.DP || {};
  DP.isDesignMode = isDesignMode;

  // ---------- Debounce ----------
  DP.debounce = function (fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  // ---------- Throttle ----------
  DP.throttle = function (fn, wait) {
    let last = 0;
    return function (...args) {
      const now = Date.now();
      if (now - last >= wait) {
        last = now;
        fn.apply(this, args);
      }
    };
  };

  // ---------- Format money (uses Shopify's money format) ----------
  DP.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = (cents / 100).toFixed(2);
    const [whole, decimal] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = `${withCommas}.${decimal}`;
    if (!format) return `$${formatted}`;
    return format.replace(/\{\{\s*amount\s*\}\}/, formatted);
  };

  // ---------- Cart AJAX ----------
  //
  // get() is memoized. Every module that shows cart state (count badge, the
  // three free-shipping widgets, GWP reconcile, mini-cart, drawer) used to call
  // it independently at init, so a single product pageview fired 15 identical
  // /cart.js requests. Measured: 90k/day against ~1200 sessions.
  //
  // Correctness rests on invalidating aggressively. Two things mutate the cart
  // without going through DP.cart.*: the product-atc handler POSTs
  // /cart/add.js with a raw fetch, and Littledata POSTs /cart/update.js on
  // every pageview to stamp __ld_attr. Neither dispatches dp:cart:updated, so
  // an event-only cache would hand them a stale cart and the count badge would
  // stop updating after add-to-cart. Hook the transport instead — then any
  // cart mutation invalidates, no matter who issued it.
  let _cached = null;    // last known cart, or null when unknown
  let _inflight = null;  // shared promise so concurrent callers make one request

  const invalidate = () => { _cached = null; _inflight = null; };
  const MUTATES = /\/cart\/(add|update|change|clear)(\.js|\.json)?(\?|$)/;

  document.addEventListener('dp:cart:updated', (e) => {
    if (e.detail && typeof e.detail.item_count === 'number') _cached = e.detail;
    else invalidate();
  });

  // Invalidate on any cart-mutating request, including ones we don't own.
  // Wrapped once, and only ever drops cached state, so a wrapper installed by
  // an app on top of ours cannot make this less correct.
  const _fetch = window.fetch;
  window.fetch = function (...args) {
    const url = String(args[0] && args[0].url ? args[0].url : args[0]);
    const hit = MUTATES.test(url);
    return _fetch.apply(this, args).then((res) => { if (hit) invalidate(); return res; });
  };
  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (MUTATES.test(String(url))) this.addEventListener('loadend', invalidate);
    return _open.call(this, method, url, ...rest);
  };

  DP.cart = {
    // force = true skips the cache. Use it after a mutation whose response you
    // did not capture; anything reading for display should leave it off.
    get(force) {
      if (!force && _cached) return Promise.resolve(_cached);
      if (!force && _inflight) return _inflight;
      const p = fetch('/cart.js', { credentials: 'same-origin' })
        .then(r => r.ok ? r.json() : r.json().then(err => Promise.reject(err)))
        .then((cart) => { _cached = cart; if (_inflight === p) _inflight = null; return cart; })
        .catch((err) => { if (_inflight === p) _inflight = null; throw err; });
      if (!force) _inflight = p;
      return p;
    },
    add(items) {
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: Array.isArray(items) ? items : [items] })
      }).then(r => r.ok ? r.json() : r.json().then(err => Promise.reject(err)))
        // /cart/add.js returns ONLY the added line item(s) — no item_count and no
        // full items list — so re-fetch the whole cart before broadcasting, or
        // consumers (drawer render, cart count, shipping bar) get a partial cart
        // and blank out. (/cart/update.js + /cart/change.js already return full.)
        .then(() => DP.cart.get(true))
        .then(cart => {
          document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: cart, bubbles: true }));
          return cart;
        });
    },
    update(updates) {
      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(updates)
      }).then(r => r.ok ? r.json() : r.json().then(err => Promise.reject(err)))
        .then(cart => {
          document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: cart, bubbles: true }));
          return cart;
        });
    },
    change(id, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity })
      }).then(r => r.ok ? r.json() : r.json().then(err => Promise.reject(err)))
        .then(cart => {
          document.dispatchEvent(new CustomEvent('dp:cart:updated', { detail: cart, bubbles: true }));
          return cart;
        });
    }
  };

  // ---------- Reveal animations (IntersectionObserver) ----------
  // In design mode, IntersectionObserver inside the editor iframe fires
  // unreliably — sections get injected but may never intersect the viewport,
  // leaving opacity:0 "blank blocks" until you save. Force-visible there.
  DP.initReveals = function (scope) {
    const root = scope || document;
    const nodes = root.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    if (nodes.length === 0) return;
    if (isDesignMode || !('IntersectionObserver' in window)) {
      nodes.forEach(n => n.classList.add('visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          entry.target.addEventListener('transitionend', () => {
            entry.target.style.willChange = 'auto';
          }, { once: true });
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });
    nodes.forEach(n => io.observe(n));
  };

  // ---------- Module registry (dp-theme.js appends here) ----------
  DP.modules = DP.modules || [];
  DP._initialized = false;
  DP.register = function (name, init) {
    DP.modules.push({ name, init });
    // If we've already initialized (e.g. module file loaded late, or a
    // shopify:section:load injected new markup), run the module now too.
    if (DP._initialized) {
      try { init(); }
      catch (err) { console.error(`[dp] module ${name} failed on late register:`, err); }
    }
  };
  DP.init = function () {
    DP._initialized = true;
    DP.initReveals();
    DP.modules.forEach((m) => {
      try { m.init(); }
      catch (err) { console.error(`[dp] module ${m.name} failed:`, err); }
    });
  };

  // ---------- Delegated data-dp-action dispatch ----------
  DP.actions = {};
  DP.bindAction = function (name, handler) { DP.actions[name] = handler; };
  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-dp-action]');
    if (!el) return;
    const name = el.getAttribute('data-dp-action');
    if (DP.actions[name]) {
      e.preventDefault();
      DP.actions[name](el, e);
    } else {
      console.warn(`[dp] no action registered for "${name}"`);
    }
  });

  // ---------- Boot ----------
  // With `defer` scripts, dp-base.js runs BEFORE dp-theme.js has pushed
  // its modules into the registry. If we called DP.init() synchronously
  // here (readyState === 'interactive' at that point), the module list
  // would still be empty. Always wait for DOMContentLoaded — by then all
  // deferred scripts have executed and the registry is complete.
  if (document.readyState === 'complete') {
    DP.init();
  } else {
    document.addEventListener('DOMContentLoaded', DP.init);
  }

  // ---------- Theme editor: re-run reveals + modules on section load ----------
  document.addEventListener('shopify:section:load', (e) => {
    DP.initReveals(e.target);
    DP.modules.forEach((m) => {
      try { m.init(); }
      catch (err) { console.error(`[dp] module ${m.name} failed on section:load:`, err); }
    });
  });
  // When a section is selected/deselected, force reveals visible inside it
  // so editing doesn't leave elements stuck at opacity:0.
  ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select', 'shopify:block:deselect'].forEach((evt) => {
    document.addEventListener(evt, (e) => {
      if (!e.target || !e.target.querySelectorAll) return;
      e.target.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale')
        .forEach(n => n.classList.add('visible'));
    });
  });
})();
